<template>
  <div class="row">
    <div class="col-md-12">
      <div
        class="d-flex flex-wrap flex-column flex-sm-row mb-3 justify-content-between"
      >
        <div>
          <div class="d-flex align-items-center">
            <div class="s-32 font-weight-bold text-head text-black">
              Asset Category
            </div>
          </div>
          <BackRoute />
        </div>

        <div class="d-flex flex-column flex-sm-row link">
          <el-button
            :color="primarycolor"
            class="header-btn w-100 mt-3 mt-sm-0"
            round
            @click="openAddParent"
          >
            Add Category
          </el-button>
        </div>
      </div>
    </div>
    <div class="col-12 px-0 table-container">
      <!-- SEARCH -->
      <div class="top-con">
        <div class="table-top py-3 mt-5 d-flex justify-content-end">
          <div class="col-md-5 px-0">
            <el-input
              size="small"
              v-model="searchText"
              placeholder="Search category..."
              @keyup.enter.prevent="loadCategories"
            >
              <template #append>
                <el-button @click.prevent="loadCategories">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </div>
        </div>
      </div>

      <!-- TABLE -->
      <div class="screensize">
        <el-table
          :data="tableData"
          row-key="id"
          class="groupTree grouptext border-0 fw-500"
          lazy
          v-loading="loading"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
          style="width: 100%"
        >
          <!-- NAME -->
          <el-table-column label="Name" width="40%">
            <template #default="scope">
              <div class="c-pointer" @click="openEdit(scope.row)">
                {{ scope.row.name }}
              </div>
            </template>
          </el-table-column>

          <!-- ITEMS COUNT -->
          <el-table-column label="Description" width="40%">
            <template #default="scope">
              <span class="s-14 fw-400"> {{ scope.row.description }}</span>
            </template>
          </el-table-column>

          <!-- ACTION -->
          <el-table-column label="Action" width="20%">
            <template #default="scope">
              <el-dropdown trigger="click">
                <el-icon><MoreFilled /></el-icon>

                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="openEdit(scope.row)">
                      Edit
                    </el-dropdown-item>
                    <el-dropdown-item @click="openAddSub(scope.row)">
                      Add Subcategory
                    </el-dropdown-item>
                    <el-dropdown-item @click="confirmDelete(scope.row)">
                      Delete
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- PAGINATION -->
      <div class="d-flex justify-content-end my-3">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          background
          :total="totalCount"
          @current-change="loadCategories"
          @size-change="loadCategories"
        />
      </div>
    </div>
  </div>

  <!-- MODAL -->
  <el-dialog
    v-model="showModal"
    :title="null"
    :width="mdAndUp || lgAndUp || xlAndUp ? '50%' : '90%'"
    class="border-radius-8"
    :close-on-click-modal="false"
  >
    <div class="p-4">
      <h5 class="font-weight-600 s-24 text-dak mb-4">
        {{ isEditing ? "Edit" : "Create" }}
        {{ isSubCategory ? "Sub-category" : "Category" }}
      </h5>

      <!-- PARENT CATEGORY -->
      <div v-if="isSubCategory" class="row mt-3 align-items-center">
        <div class="col-md-3 text-md-right text-left">
          <label class="fw-400 s-16 text-dak">Parent Category</label>
        </div>
        <div class="col-md-9">
          <el-select-v2
            v-model="assetCategoryId"
            @change="setSelectedAssetCategory"
            :options="
              assetCategoryData.map((i) => ({ label: i.name, value: i.id }))
            "
            placeholder="Select"
            size="large"
            class="w-100"
          />
        </div>
      </div>

      <!-- NAME -->
      <div class="row mt-3 align-items-center">
        <div class="col-md-3 text-md-right text-left">
          <label class="fw-400 s-16 text-dak">Name</label>
        </div>
        <div class="col-md-9">
          <el-input
            v-model="form.name"
            size="large"
            placeholder="Enter name"
            class="w-100"
          />
        </div>
      </div>

      <!-- DESCRIPTION -->
      <div class="row mt-3 align-items-center">
        <div class="col-md-3 text-md-right text-left">
          <label class="fw-400 s-16 text-dak">Description</label>
        </div>
        <div class="col-md-9">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            placeholder="Enter description"
            class="w-100"
          />
        </div>
      </div>

      <!-- BUTTONS -->
      <div class="row mt-4 justify-content-center align-items-center">
        <div class="col-md-5">
          <el-button
            round
            :color="primarycolor"
            class="w-100 text-white"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="saveRecord"
          >
            {{ isEditing ? "Update" : "Create" }}
          </el-button>

          <el-button round link class="w-100 text-dak mt-2" @click="closeModal">
            Cancel
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, inject, onMounted } from "vue";
import assetService from "@/services/asset/assetServices";
import { ElNotification, ElMessageBox } from "element-plus";
import BackRoute from "../../components/backroute/BackRoute.vue";
import deviceBreakpoint from "../../mixins/deviceBreakpoint";

const { mdAndUp, lgAndUp, xlAndUp } = deviceBreakpoint();
const primarycolor = inject("primarycolor");

const loading = ref(false);
const tableData = ref([]);
const assetCategoryData = ref([]);
const totalCount = ref(0);
const searchText = ref("");
const page = ref(1);
const pageSize = ref(10);

const showModal = ref(false);
const modalTitle = ref("Category");
const isEditing = ref(false);
const isSubCategory = ref(false);
const isLoading = ref(false);
const assetCategoryId = ref(null);
const isSubmitting = ref(false);
const selectedAssetCategory = ref({});

const form = ref({
  id: null,
  name: "",
  description: "",
  parentId: null,
});

const resetForm = () => {
  form.value.id = null;
  form.value.name = "";
  form.value.description = "";
  form.value.parentId = null;
};
const closeModal = () => {
  showModal.value = false;
  isEditing.value = false;
};

/* ---------------------------
   LOAD CATEGORIES
--------------------------- */
const loadCategories = async () => {
  loading.value = true;
  try {
    let res = await assetService.getAllCategory();
    let list = res || [];

    // Search filter
    if (searchText.value) {
      list = list.filter((x) =>
        x.name.toLowerCase().includes(searchText.value.toLowerCase())
      );
    }

    totalCount.value = list.length;

    // Pagination
    const start = (page.value - 1) * pageSize.value;
    const end = start + pageSize.value;

    const pageItems = list.slice(start, end);

    // Ensure children array exists
    pageItems.forEach((item) => {
      item.children = item.children || [];
    });

    tableData.value = pageItems;
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
};

onMounted(loadCategories);

/* -----------------------
   OPEN MODALS
----------------------- */
const openAddParent = () => {
  resetForm();
  isEditing.value = false;
  isSubCategory.value = false;
  modalTitle.value = "Add Category";
  showModal.value = true;
};

const openAddSub = (parent) => {
  resetForm();
  isEditing.value = false;
  isSubCategory.value = true;
  form.value.parentId = parent.id;
  modalTitle.value = `Add Subcategory to ${parent.name}`;
  showModal.value = true;
};

const openEdit = (item) => {
  isEditing.value = true;
  isSubCategory.value = !!item.parentId;

  form.value.id = item.id;
  form.value.name = item.name;
  form.value.description = item.description || "";
  form.value.parentId = item.parentId || null;

  modalTitle.value = isSubCategory.value ? "Edit Subcategory" : "Edit Category";
  showModal.value = true;
};

/* -----------------------
       SAVE RECORD
----------------------- */
const saveRecord = async () => {
  if (!form.value.name.trim()) {
    ElNotification.warning("Name is required");
    return;
  }

  isSubmitting.value = true;
  loading.value = true;
  try {
    if (!form.value.id) {
      // CREATE
      if (form.value.parentId) {
        await assetService.createSubCategory(form.value.parentId, {
          name: form.value.name,
          description: form.value.description,
        });
      } else {
        await assetService.createCategory({
          name: form.value.name,
          description: form.value.description,
        });
      }
      ElNotification.success("Created successfully!");
    } else {
      // UPDATE
      if (form.value.parentId) {
        await assetService.updateSubCategory(form.value.id, form.value.parentId, {
          name: form.value.name,
          description: form.value.description,
        });
      } else {
        await assetService.updateCategory(form.value.id, {
          name: form.value.name,
          description: form.value.description,
        });
      }
      ElNotification.success("Updated successfully!");
    }

    showModal.value = false;
    loadCategories();
  } catch (err) {
    console.error(err);
    ElNotification.error("Error saving record");
  } finally {
    loading.value = false;
  }
};
const loadCategory = async () => {
  isLoading.value = true;
  try {
    const response = await assetService.getCategories();
    assetCategoryData.value = response || [];
  } catch {
    ElNotification.error("Failed to load categories");
  } finally {
    isLoading.value = false;
  }
};
loadCategory();
const setSelectedAssetCategory = () => {
  selectedAssetCategory.value = assetCategoryData.value.find(
    (i) => i.id === assetCategoryId.value
  );
};

/* -----------------------
       DELETE RECORD
----------------------- */
const confirmDelete = (item) => {
  ElMessageBox.confirm(
    `Are you sure you want to Delete "${item.name}"?`,
    "Confirm delete",
    {
      type: "error",
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
    }
  ).then(async () => {
    try {
      if (item.parentId) {
        await assetService.deleteSubCategory(item.id, item.parentId);
      } else {
        await assetService.deleteCategory(item.id);
      }
      ElNotification.success("Deleted successfully!");
      loadCategories();
    } catch (err) {
      console.error(err);
      ElNotification.error("Error deleting record");
    }
  });
};
</script>

<style scoped>
.groupText
  .el-table__inner-wrapper
  .el-table__header-wrapper
  .el-table__header
  thead
  tr
  th
  .el-table_1_column_1
  .is-leaf
  .el-table__cell {
  /* border-bottom: var(--el-table-border); */
  font-weight: 500 !important;
}
.c-pointer {
  cursor: pointer;
}
@media screen and (max-width: 600px) {
  .table-container {
    overflow: auto;
  }

  .screensize,
  .table-top {
    min-width: 500px;
  }
}

@media screen and (min-width: 580px) {
  .hidden-header {
    display: none;
  }
}

@media screen and (max-width: 581px) {
  .main-header {
    display: none;
  }

  .yu {
    margin-top: 3rem !important;
  }

  .f-right {
    float: right;
  }
}
</style>

<!-- <template>
  <div class="container-slim mt-5">
  
    <div
      class="d-flex flex-wrap flex-column flex-sm-row mb-3 justify-content-between"
    >
      <div>
        <div class="d-flex align-items-center">
          <div class="s-32 font-weight-bold text-head text-black">
            Asset Category
          </div>
        </div>
        <BackRoute />
      </div>

      <div class="d-flex flex-column flex-sm-row link">
        <el-button
          :color="primarycolor"
          class="header-btn w-100 mt-3 mt-sm-0"
          round
          :loading="isLoading"
          :disabled="isLoading"
          @click="openModal(false)"
        >
          Add Category
        </el-button>
      </div>
    </div>


    <div class="row">
      <div class="container-fluid mt-5">
        <el-skeleton :loading="isLoading" animated rows="5">
          <TableNoExport2
            :data="filteredCategory"
            :headers="AssetCategoryHeaders"
            :textHead="textHead"
            @update:searchText="handleCategorySearchTextUpdate"
            :checkMultipleItem="false"
            :filterItem="false"
          >
            <template v-slot:name="{ item }">
              <div class="c-pointer fw-400 s-14">{{ item.name }}</div>
            </template>

            <template v-slot:description="{ item }">
              <div class="c-pointer fw-400 wrap-text s-14">
                {{ item.description }}
              </div>
            </template>

            <template v-slot:action="{ item }">
              <el-dropdown trigger="click">
                <el-icon><MoreFilled /></el-icon>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item>
                      <div class="text-color" @click="deleteCategory(item.id)">
                        Delete
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item @click="editItem(item, false)">
                      <div class="text-color">Edit</div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </TableNoExport2>
        </el-skeleton>
      </div>
    </div>

 
    <div class="row">
      <div class="col-md-12 mt-5">
        <div class="d-flex flex-sm-row mb-3 justify-content-end">
          <el-button
            class="header-btn secondary-button mt-3 mt-sm-0"
            round
            :loading="isLoading"
            :disabled="isLoading"
            @click="openModal(true)"
          >
            Add Subcategory
          </el-button>
        </div>
      </div>

      <div class="container-fluid">
        <div class="col-md-12 px-0 mt-3">
          <el-skeleton :loading="isLoading" animated rows="5">
            <TableNoExport2
              :data="filteredSubCategory"
              :headers="SubCategoryHeaders"
              :textHead="textHead2"
              @update:searchText="handleSubCategorySearchTextUpdate"
              :checkMultipleItem="false"
              :filterItem="false"
            >
              <template v-slot:name="{ item }">
                <div class="c-pointer fw-400 s-14">{{ item.name }}</div>
              </template>
              <template v-slot:description="{ item }">
                <div class="c-pointer fw-400 s-14">{{ item.description }}</div>
              </template>
              <template v-slot:action="{ item }">
                <el-dropdown trigger="click">
                  <el-icon><MoreFilled /></el-icon>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item>
                        <div
                          class="text-color"
                          @click="deleteSubCategory(item.id, item.parentId)"
                        >
                          Delete
                        </div>
                      </el-dropdown-item>
                      <el-dropdown-item @click="editItem(item, true)">
                        <div class="text-color">Edit</div>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </template>
            </TableNoExport2>
          </el-skeleton>
        </div>
      </div>
    </div>

  
    <el-dialog
      v-model="displayModal"
      :title="null"
      :width="mdAndUp || lgAndUp || xlAndUp ? '50%' : '90%'"
      class="border-radius-8"
      :close-on-click-modal="false"
    >
      <div class="p-4">
        <h5 class="font-weight-600 s-24 text-dak mb-4">
          {{ isEditing ? "Edit" : "Create" }}
          {{ isSubCategory ? "Sub-category" : "Category" }}
        </h5>

        <div v-if="isSubCategory" class="row mt-3 align-items-center">
          <div class="col-md-3 text-md-right text-left">
            <label class="fw-400 s-16 text-dak">Parent Category</label>
          </div>
          <div class="col-md-9">
            <el-select-v2
              v-model="assetCategoryId"
              @change="setSelectedAssetCategory"
              :options="
                assetCategoryData.map((i) => ({ label: i.name, value: i.id }))
              "
              placeholder="Select"
              size="large"
              class="w-100"
            />
          </div>
        </div>

        <div class="row mt-3 align-items-center">
          <div class="col-md-3 text-md-right text-left">
            <label class="fw-400 s-16 text-dak">Name</label>
          </div>
          <div class="col-md-9">
            <el-input
              v-model="form.name"
              size="large"
              placeholder="Enter name"
              class="w-100"
            />
          </div>
        </div>

        <div class="row mt-3 align-items-center">
          <div class="col-md-3 text-md-right text-left">
            <label class="fw-400 s-16 text-dak">Description</label>
          </div>
          <div class="col-md-9">
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="3"
              placeholder="Enter description"
              class="w-100"
            />
          </div>
        </div>

        <div class="row mt-4 justify-content-center align-items-center">
          <div class="col-md-5">
            <el-button
              round
              :color="primarycolor"
              class="w-100 text-white"
              :loading="isSubmitting"
              :disabled="isSubmitting"
              @click="submitCategory"
            >
              {{ isEditing ? "Update" : "Create" }}
            </el-button>
            <el-button
              round
              link
              class="w-100 text-dak mt-2"
              @click="closeModal"
            >
              Cancel
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted } from "vue";
import { ElNotification, ElMessageBox } from "element-plus";
import router from "../../router";
import BackRoute from "../../components/backroute/BackRoute.vue";
import TableNoExport2 from "../../components/table/TableNoExport2.vue";
import assetService from "@/services/asset/assetServices";
import deviceBreakpoint from "../../mixins/deviceBreakpoint";

const { mdAndUp, lgAndUp, xlAndUp } = deviceBreakpoint();
const primarycolor = inject("primarycolor");

const displayModal = ref(false);
const isSubCategory = ref(false);
const isEditing = ref(false);
const isLoading = ref(false);
const isSubmitting = ref(false);

const assetCategoryId = ref(null);
const selectedAssetCategory = ref({});
const assetCategoryData = ref([]);
const subCategoryData = ref([]);
const searchCategoryText = ref("");
const searchSubCategoryText = ref("");
const form = ref({ name: "", description: "", id: "" });

const textHead = ref("Category");
const textHead2 = ref("Sub-category");

const AssetCategoryHeaders = ref([
  { name: "Name", value: "name" },
  { name: "Description", value: "description" },
  { name: "Action", value: "action" },
]);

const SubCategoryHeaders = ref([
  { name: "Name", value: "name" },
  { name: "Description", value: "description" },
  { name: "Action", value: "action" },
]);

// Filtering
const filteredCategory = computed(() => {
  if (!searchCategoryText.value.trim()) return assetCategoryData.value;
  return assetCategoryData.value.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchCategoryText.value.toLowerCase())
  );
});

const filteredSubCategory = computed(() => {
  if (!searchSubCategoryText.value.trim()) return subCategoryData.value;
  return subCategoryData.value.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchSubCategoryText.value.toLowerCase())
  );
});
const handleSubCategorySearchTextUpdate = (newValue) => {
  searchSubCategoryText.value = newValue;
};
const handleCategorySearchTextUpdate = (newValue) => {
  searchCategoryText.value = newValue;
};

// Modal
const openModal = (sub = false) => {
  isSubCategory.value = sub;
  isEditing.value = false;
  displayModal.value = true;
  form.value = { name: "", description: "", id: "" };
  assetCategoryId.value = null;
};

const closeModal = () => {
  displayModal.value = false;
  isEditing.value = false;
};

// ===================== API CALLS =====================
const loadCategories = async () => {
  isLoading.value = true;
  try {
    const response = await assetService.getAllCategory();
    assetCategoryData.value = response || [];
  } catch {
    ElNotification.error("Failed to load categories");
  } finally {
    isLoading.value = false;
  }
};

const loadSubCategories = async () => {
  isLoading.value = true;
  try {
    const categories = await assetService.getCategories();
    const allSubs = [];

    for (const cat of categories) {
      const subs = await assetService.getSubCategories(cat.id);
      subs.forEach((sub) =>
        allSubs.push({ ...sub, category: cat.name, categoryId: cat.id })
      );
    }
    subCategoryData.value = allSubs;
  } catch {
    ElNotification.error("Failed to load subcategories");
  } finally {
    isLoading.value = false;
  }
};

// ===================== SUBMIT =====================
const submitCategory = async () => {
  if (!form.value.name.trim()) {
    ElNotification.warning("Name is required");
    return;
  }

  isSubmitting.value = true;
  try {
    const payload = {
      name: form.value.name,
      description: form.value.description,
    };

    if (isEditing.value) {
      // ================= EDIT MODE =================
      if (isSubCategory.value && selectedAssetCategory.value.id) {
        await assetService.updateSubCategory(
          form.value.id,
          selectedAssetCategory.value.id,
          payload
        );
      } else {
        await assetService.updateCategory(form.value.id, payload);
      }
    } else {
      // ================= CREATE MODE =================
      if (isSubCategory.value && selectedAssetCategory.value.id) {
        await assetService.createSubCategory(
          selectedAssetCategory.value.id,
          payload
        );
      } else {
        await assetService.createCategory(payload);
      }
    }

    ElNotification.success(
      `${isEditing.value ? "Updated" : "Created"} successfully`
    );
    closeModal();
    await loadCategories();
    await loadSubCategories();
  } catch (error) {
    console.error("Error saving category:", error);
    ElNotification.error("Failed to save category");
  } finally {
    isSubmitting.value = false;
  }
};

// ===================== EDIT =====================
const editItem = (item, isSub) => {
  isSubCategory.value = isSub;
  isEditing.value = true;
  displayModal.value = true;

  form.value = {
    name: isSub ? item.subCategory : item.name,
    description: item.description,
    id: item.id,
  };

  if (isSub && item.categoryId) {
    assetCategoryId.value = item.categoryId;
    setSelectedAssetCategory();
  }
};

const setSelectedAssetCategory = () => {
  selectedAssetCategory.value = assetCategoryData.value.find(
    (i) => i.id === assetCategoryId.value
  );
};

// ===================== DELETE =====================
const deleteCategory = async (id) => {
  try {
    await ElMessageBox.confirm("Delete this category?", "Confirm", {
      type: "warning",
    });
    await assetService.deleteCategory(id);
    ElNotification.success("Category deleted");
    loadCategories();
  } catch {}
};

const deleteSubCategory = async (id, parentId) => {
  try {
    await ElMessageBox.confirm("Delete this sub-category?", "Confirm", {
      type: "warning",
    });
    await assetService.deleteSubCategory(id, parentId);
    ElNotification.success("Sub-category deleted");
    loadSubCategories();
  } catch {}
};

onMounted(() => {
  loadCategories();
  loadSubCategories();
});
</script>

<style scoped>
.wrap-text {
  white-space: normal !important;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 60%;
}
</style> -->
